import { useEffect, useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import RichText from "@/components/RichText";
import { resolveButtonStyle } from "@/lib/ctaButtonStyle";
import ContactFormModal from "./ContactFormModal";

// Variante "fenêtre" de la section Contact (ContactSection.jsx) : mêmes
// coordonnées, même formulaire, même endpoint /contact-request en sortie —
// mais le formulaire ne prend plus la moitié de la page, il s'ouvre dans une
// fenêtre au clic (voir ContactFormModal.jsx pour la fenêtre elle-même).
// Section RÉPÉTABLE et disponible sur toutes les pages, contrairement au
// singleton historique "contact" : c'est un appel à l'action, pas le pied de
// page d'une page d'accueil.
//
// Valeurs par défaut identiques à DEFAULT_PROPS["contact_modal"] côté backend
// — reprises ici pour que la section reste rendue correctement même si
// l'API n'a pas encore répondu (voir HeroSection.jsx pour la même logique).
const DEFAULTS = {
  eyebrow: "",
  heading: "Un projet en tête ?",
  subheading: "Décrivez votre besoin, on revient vers vous sous 24h avec une estimation concrète.",
  email: "",
  phone: "",
  address: "",
  cta_label: "Nous contacter",
  variant: "dark",
  modal_heading: "Parlons de votre projet",
  modal_subheading: "Réponse sous 24h avec une estimation.",
  modal_submit_label: "Envoyer ma demande",
  message_placeholder: "Décrivez votre besoin…",
  confirmation_message: "Merci ! Votre demande est bien arrivée, on revient vers vous sous 24h.",
  show_phone_field: "true",
  form_style: "simple",
  quote_options: "Auto, Habitation, Santé, Prévoyance",
  anchor_id: "",
};

export default function ContactModalSection({ props = {} }) {
  const t = { ...DEFAULTS, ...props };
  const [open, setOpen] = useState(false);
  const dark = t.variant === "dark";
  const anchorId = t.anchor_id || "";

  // Un lien de menu du type "/#devis" (l'ancre de CETTE section) ouvre
  // directement la fenêtre, plutôt que de déposer le visiteur devant un
  // bandeau sur lequel il doit encore cliquer. Sans ancre configurée, rien
  // ne se déclenche jamais tout seul.
  useEffect(() => {
    if (!anchorId) return undefined;
    const openIfTargeted = () => {
      if (window.location.hash === `#${anchorId}`) setOpen(true);
    };
    openIfTargeted();
    window.addEventListener("hashchange", openIfTargeted);
    return () => window.removeEventListener("hashchange", openIfTargeted);
  }, [anchorId]);

  // Bouton neuf, sans rendu historique à préserver : en mode "auto" il
  // affiche l'enveloppe (le mode "Texte seul" la retire, un autre choix
  // d'icône la remplace) — voir ctaButtonStyle.js.
  const ctaStyle = resolveButtonStyle(t, "cta", { fallbackIcon: "Mail" });
  const telHref = `tel:${(t.phone || "").replace(/[^\d+]/g, "")}`;
  const details = [
    t.email && { key: "email", Icon: Mail, label: t.email, href: `mailto:${t.email}` },
    t.phone && { key: "phone", Icon: Phone, label: t.phone, href: telHref },
    t.address && { key: "address", Icon: MapPin, label: t.address, href: null },
  ].filter(Boolean);

  return (
    <section
      id={anchorId || undefined}
      data-testid="contact-modal-section"
      className={`scroll-mt-28 ${dark ? "bg-ink" : "bg-sand"}`}
    >
      <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-12">
          <div className="max-w-2xl">
            {t.eyebrow && (
              <RichText
                as="p"
                className="text-[13px] font-semibold tracking-wide uppercase text-gold mb-3"
                html={t.eyebrow}
              />
            )}
            <RichText
              as="h2"
              className={`font-display font-semibold text-[30px] md:text-[34px] leading-tight tracking-[-0.02em] ${dark ? "text-white" : "text-ink"}`}
              html={t.heading}
            />
            {t.subheading && (
              <RichText
                as="p"
                preserveParagraphs
                className={`text-[17px] mt-4 leading-relaxed ${dark ? "text-white/75" : "text-graphite"}`}
                html={t.subheading}
              />
            )}

            {details.length > 0 && (
              <div className="mt-7 flex flex-wrap gap-x-8 gap-y-3">
                {details.map(({ key, Icon, label, href }) => {
                  const content = (
                    <>
                      <span
                        className={`h-9 w-9 rounded-btn flex items-center justify-center border ${
                          dark ? "bg-white/5 border-white/15" : "bg-white border-stone"
                        }`}
                      >
                        <Icon className="h-4 w-4 text-gold" />
                      </span>
                      {label}
                    </>
                  );
                  const className = `flex items-center gap-3 text-[15px] ${
                    dark ? "text-white/85" : "text-ink"
                  } ${href ? "hover:text-gold transition-colors" : ""}`;
                  return href ? (
                    <a key={key} href={href} className={className} data-testid={`contact-modal-section-detail-${key}`}>
                      {content}
                    </a>
                  ) : (
                    <div key={key} className={className} data-testid={`contact-modal-section-detail-${key}`}>
                      {content}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {t.cta_label && (
            <div className="shrink-0">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="contact-modal-section-trigger"
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-label={!ctaStyle.showText ? t.cta_label : undefined}
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 text-[15px] font-semibold transition-opacity hover:opacity-90 ${
                  dark ? "bg-white text-ink" : "bg-ink text-white"
                } ${ctaStyle.className}`}
                style={ctaStyle.style}
              >
                {ctaStyle.showIcon && ctaStyle.Icon && <ctaStyle.Icon className="h-4 w-4" />}
                {ctaStyle.showText && t.cta_label}
              </button>
            </div>
          )}
        </div>
      </div>

      <ContactFormModal
        open={open}
        onClose={() => setOpen(false)}
        heading={t.modal_heading}
        subheading={t.modal_subheading}
        submitLabel={t.modal_submit_label}
        messagePlaceholder={t.message_placeholder}
        confirmationMessage={t.confirmation_message}
        showPhone={t.show_phone_field !== "false"}
        formStyle={t.form_style}
        quoteOptions={t.quote_options}
        fieldSettings={t}
      />
    </section>
  );
}
